import type { ApReviewQueueItem } from "@/accounts-payable/types";
import { dayjs } from "@/core/utils/date";

/** Where the review list was: a card, and how far it sat below the top. */
export interface QueuePosition {
  filterKey: string;
  invoiceIds: string[];
  invoiceIndex: number;
  invoiceCreatedAt: string;
  offset: number;
  scrollTop: number;
  pageCount: number;
}

/** Restore saved depth and search past new arrivals in the server's date order. */
export const needsMoreQueuePages = ({
  position,
  items,
  loadedPageCount,
  hasNextPage,
}: {
  position: QueuePosition;
  items: Pick<ApReviewQueueItem, "id" | "created_at">[];
  loadedPageCount: number;
  hasNextPage: boolean;
}) => {
  if (!hasNextPage) return false;
  if (loadedPageCount < position.pageCount) return true;
  const invoiceId = position.invoiceIds[position.invoiceIndex];
  if (items.some((item) => item.id === invoiceId)) return false;
  const lastItem = items[items.length - 1];
  return (
    !!lastItem &&
    !dayjs(lastItem.created_at).isBefore(dayjs(position.invoiceCreatedAt))
  );
};

/** Prefer the next surviving invoice, then the previous one at each distance. */
export const getQueueReturnIndex = (
  position: QueuePosition,
  currentIds: string[],
) => {
  const indices = new Map(currentIds.map((id, index) => [id, index]));
  for (let distance = 0; distance < position.invoiceIds.length; distance++) {
    for (const index of [
      position.invoiceIndex + distance,
      position.invoiceIndex - distance,
    ]) {
      const currentIndex = indices.get(position.invoiceIds[index]);
      if (currentIndex !== undefined) return currentIndex;
    }
  }
  return Math.min(position.invoiceIndex, currentIds.length - 1);
};

/** Cards stack top to bottom, so halving finds the first one still showing. */
export const firstCardShowing = (
  cards: ArrayLike<HTMLElement>,
  viewportTop: number,
) => {
  let low = 0;
  let high = cards.length;
  while (low < high) {
    const middle = Math.floor((low + high) / 2);
    if (cards[middle].getBoundingClientRect().bottom > viewportTop)
      high = middle;
    else low = middle + 1;
  }
  return cards[low] as HTMLElement | undefined;
};
